(() => {
  const sizes = ["XS", "S", "M", "L", "XL"];
  const normalizeKg = value => { if (Math.abs(value) > 1e12) return value; const rounded = Math.round(value * 1e9) / 1e9; return rounded === 0 && value > 0 ? value : rounded; };
  function recommend(weightKg, config, bodyType = "", breedBias = 0) {
    const kg = normalizeKg(Number(weightKg));
    if (!Number.isFinite(kg) || kg <= 0) return null;
    const baseSize = config.bands.find(b => kg < b.maxKg)?.size || "XL";
    const zone = config.optionalRefinement.zones.find(z => kg >= z.minKg && kg <= z.maxKg);
    let finalSize = baseSize;
    let reasonCode = kg < 2 ? "weight_below_public_range" : kg > 60 ? "weight_above_public_range" : "weight_only";
    if (zone) {
      if (bodyType === "slim") { finalSize = zone.smaller; reasonCode = "body_slim_down"; }
      else if (bodyType === "average") { reasonCode = "body_average_base"; }
      else if (bodyType === "broad") { finalSize = zone.larger; reasonCode = "body_broad_up"; }
      else if (breedBias === 1) { finalSize = zone.larger; reasonCode = "breed_up"; }
      else if (breedBias === -1) { finalSize = zone.smaller; reasonCode = "breed_down"; }
    }
    if (Math.abs(sizes.indexOf(finalSize) - sizes.indexOf(baseSize)) > 1) finalSize = baseSize;
    return { weightKg: kg, baseSize, finalSize, wasRefined: finalSize !== baseSize, reasonCode };
  }
  const engine = { recommend, toKg: (weight, unit) => normalizeKg(Number(weight) * (unit === "lb" ? 0.45359237 : 1)) };
  if (typeof module !== "undefined" && module.exports) module.exports = engine;
  if (typeof window === "undefined") return;
  window.FourPawSizingV2 = engine;
  if (customElements.get("fourpaw-size-finder")) return;
  customElements.define("fourpaw-size-finder", class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const on = (el, type, fn) => el?.addEventListener(type, fn, { signal: this.controller.signal });
      this.config = JSON.parse(this.querySelector("[data-sizing-config]").textContent);
      this.copy = JSON.parse(this.querySelector("[data-sizing-copy]").textContent);
      this.unit = this.dataset.defaultUnit === "kg" ? "kg" : "lb";
      this.sizeOptionName = this.normalize(this.dataset.sizeOptionName || "Size");
      this.sizeOptionPosition = Number(this.dataset.sizeOptionPosition || 2);
      this.weightInput = this.querySelector("[data-size-weight]");
      this.result = this.querySelector("[data-size-result]");
      this.refinement = this.querySelector("[data-size-refinement]");
      this.breedInput = this.querySelector("[data-size-breed-search]");
      this.dataset.sizingVersion = "2";
      this.feedback = document.createElement("div");
      this.feedback.className = "fourpaw-size-finder__feedback";
      this.feedback.hidden = true;
      this.feedback.setAttribute("role", "status");
      this.feedback.setAttribute("aria-live", "polite");
      this.feedbackTitle = document.createElement("strong");
      this.feedbackDetail = document.createElement("p");
      this.feedbackApply = document.createElement("button");
      this.feedbackApply.type = "button";
      this.feedbackApply.textContent = this.copy.apply;
      this.feedback.append(this.feedbackTitle, this.feedbackDetail, this.feedbackApply);
      this.refinement.append(this.feedback);
      on(this.feedbackApply, "click", () => this.applySize());
      on(this.querySelector("[data-size-calculate]"), "click", () => this.calculate());
      on(this.querySelector("[data-size-apply]"), "click", () => this.applySize());
      on(this.weightInput, "keydown", event => { if (event.key === "Enter") { event.preventDefault(); this.calculate(); } });
      on(this.weightInput, "input", () => { this.canonicalKg = null; if (this.hasCalculated) this.calculate("size_finder_refined"); });
      this.querySelectorAll("[data-size-unit]").forEach(button => on(button, "click", () => this.setUnit(button.dataset.sizeUnit)));
      on(this.querySelector("[data-size-refine-open]"), "click", () => {
        if (!this.refinement.hidden) { this.refinement.hidden = true; this.querySelector("[data-size-refine-open]").setAttribute("aria-expanded", "false"); return; }
        this.refinement.hidden = false; this.refinementOpened = true;
        this.querySelector("[data-size-refine-open]").setAttribute("aria-expanded", "true");
        this.track("size_refinement_opened"); this.refinement.querySelector("input")?.focus();
      });
      this.querySelectorAll("[data-size-body]").forEach(input => on(input, "change", () => this.calculate("size_finder_refined")));
      on(this.breedInput, "input", () => this.calculate("size_finder_refined"));
      on(this.querySelector("[data-size-clear]"), "click", () => {
        this.querySelectorAll("[data-size-body]").forEach(input => { input.checked = false; });
        this.breedInput.value = ""; this.calculate("size_finder_refined");
      });
      this.querySelectorAll("[data-size-unit]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.sizeUnit === this.unit)));
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; }
    normalize(value) { return String(value).normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toLowerCase(); }
    parseWeight() { return Number(String(this.weightInput.value).trim().replace(",", ".")); }
    setUnit(unit) {
      if (unit === this.unit) return;
      const kg = this.canonicalKg ?? engine.toKg(this.parseWeight(), this.unit);
      this.unit = unit;
      if (Number.isFinite(kg) && kg > 0) {
        this.canonicalKg = kg;
        this.weightInput.value = String(Math.round((unit === "lb" ? kg / 0.45359237 : kg) * 1000) / 1000);
      }
      this.querySelector("[data-size-unit-label]").textContent = unit;
      this.querySelectorAll("[data-size-unit]").forEach(b => b.setAttribute("aria-pressed", String(b.dataset.sizeUnit === unit)));
      if (this.hasCalculated) this.calculate("size_finder_refined");
    }
    calculate(eventName = "size_finder_calculated") {
      const kg = this.canonicalKg ?? engine.toKg(this.parseWeight(), this.unit);
      const bodyType = this.querySelector("[data-size-body]:checked")?.value || "";
      const breed = this.config.breedProfiles.find(p => this.normalize(p.breed) === this.normalize(this.breedInput.value));
      const previousSize = this.lastResult?.finalSize;
      const result = engine.recommend(kg, this.config, bodyType, breed?.boundaryBias || 0);
      this.canApplyRecommendation = Boolean(result);
      this.recommendedSize = result?.finalSize || "";
      this.weightInput.setAttribute("aria-invalid", String(!result));
      const error = this.querySelector("[data-size-error]"); error.hidden = Boolean(result);
      if (!result) { this.feedback.hidden = true; this.result.hidden = true; this.lastResult = null; return; }
      this.hasCalculated = true;
      this.lastResult = { ...result, weightUnit: this.unit, refinementOpened: Boolean(this.refinementOpened), bodyType, breed: breed?.breed || "", breedBias: breed?.boundaryBias || 0 };
      this.result.hidden = false;
      this.querySelector("[data-size-result-size]").textContent = result.finalSize;
      let copy = this.copy.weightOnly;
      if (result.reasonCode === "weight_below_public_range") copy = this.copy.belowMinimum;
      else if (result.reasonCode === "weight_above_public_range") copy = this.copy.aboveMaximum;
      else if (bodyType && result.reasonCode.startsWith("body_")) copy = this.copy[bodyType + "Result"];
      else if (result.reasonCode.startsWith("breed_")) copy = this.copy.breedResult;
      this.querySelector("[data-size-result-copy]").textContent = copy;
      this.feedback.hidden = !(bodyType || breed);
      if (!this.feedback.hidden) {
        const changed = previousSize && previousSize !== result.finalSize;
        this.feedbackTitle.textContent = (changed ? this.copy.refinedChanged : this.copy.refinedSame)
          .replace("{from}", previousSize || result.baseSize).replace("{size}", result.finalSize);
        const choices = [bodyType ? this.copy[bodyType] : "", breed?.breed || ""].filter(Boolean).join(" · ");
        this.feedbackDetail.textContent = choices + (bodyType && breed ? " — " + this.copy.bodyPriority : "");
        this.feedbackApply.textContent = this.copy.applyNamed.replace("{size}", result.finalSize);
      }
      this.track(eventName);
    }
    track(name) { window.FourPawAnalytics?.publish("fourpaw:" + name, this.lastResult ? { ...this.lastResult, refinementOpened: Boolean(this.refinementOpened) } : {}); }
      applySize() {
        if (!this.recommendedSize || !this.canApplyRecommendation) return;
        const sectionId = this.dataset.sectionId;
        const productForm = sectionId
          ? document.getElementById(`product-form-${sectionId}`)
          : null;
        const scope =
          (sectionId && document.getElementById(`shopify-section-${sectionId}`)) ||
          productForm?.closest(".shopify-section") ||
          productForm ||
          document;
        const radios = Array.from(
          scope.querySelectorAll(
            '.simple-variant-picker input[type="radio"][name^="options["]',
          ),
        );
        const sizeRadio = radios.find((radio) => {
          const optionName = radio.name.toLowerCase();
          const matchesPosition =
            Number.isInteger(this.sizeOptionPosition) &&
            this.sizeOptionPosition > 0 &&
            Number(radio.dataset.optionPosition) === this.sizeOptionPosition;
          return (
            (matchesPosition || optionName.includes(this.sizeOptionName)) &&
            String(radio.value).trim().toUpperCase() === this.recommendedSize.toUpperCase()
          );
        });
        if (!sizeRadio || sizeRadio.disabled) return;

        this.track("size_finder_applied");
        sizeRadio.checked = true;
        sizeRadio.dispatchEvent(new Event("change", { bubbles: true }));
        this.closest(".size-chart-popup")?.querySelector(".size-chart-popup-close")?.click();
        window.setTimeout(() => {
          scope.querySelector(`label[for="${CSS.escape(sizeRadio.id)}"]`)?.focus?.();
        }, 100);
      }
  });
})();
